import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Drawer,
  Icon,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
  Divider,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  HomeOutlined,
  ChevronLeft,
  LogoutOutlined,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppThemeContext, useUserContext, useMenuContext, UserRoleEnum } from '../contexts';
import { SconIcon } from '../assets';
import { AlertComponent } from '../components/MUI/alert/AlertComponent';
import { UpgradeModal } from '../components/UpgradeModal/UpgradeModal';
import { UserMenu } from '../components/SconApp/userMenu/UserMenu';
import { IosPwaBanner } from '../components/IosPwaBanner';
import { FeedbackButton } from '../components/FeedbackButton/FeedbackButton';
import { BottomNav } from '../components/SconApp/BottomNav/BottomNav';
import { Environment } from '../../shared/environment';

type Props = {
  children?: React.ReactNode;
  toolbar?: React.ReactNode;
};

export const LayoutShell: React.FC<Props> = ({ children, toolbar }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme, isMobile, isTablet } = useAppThemeContext();
  const { isAuthenticated, logout, emailConfirmed, phoneConfirmed, whatsappSconappConfigured, userRoles } = useUserContext();
  const { menuOptions } = useMenuContext();

  const [sidebarOpen, setSidebarOpen] = useState(!isTablet);
  const hasBusinessAdmin = userRoles.includes(UserRoleEnum.BusinessAdmin);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => ({
    '/business': true,
    ...(!hasBusinessAdmin ? { '/employee': true } : {}),
  }));

  const isDev = Environment.isDevelopment;

  useEffect(() => {
    if (isDev) {
      // Draw a DEV favicon on a canvas and replace the default one
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#e91e63';
        ctx.fillRect(0, 0, 32, 32);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px "Segoe UI", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('DEV', 16, 16);
      }
      const link = document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
      if (link) {
        link.href = canvas.toDataURL('image/png');
      }
    }
  }, [isDev]);

  interface SidebarNavOption {
    icon: React.ReactNode;
    label: string;
    path: string;
    page?: number;
    children?: SidebarNavOption[];
  }

  const buildNavOptions = (options: typeof menuOptions): SidebarNavOption[] => {
    return options.map((opt) => ({
      icon: <Icon>{opt.icon}</Icon>,
      label: opt.label,
      path: opt.path,
      page: opt.page,
      children: opt.children ? buildNavOptions(opt.children) : undefined,
    }));
  };

  const navOptions: SidebarNavOption[] = [
    {
      icon: <HomeOutlined />,
      label: 'Início',
      path: '/home',
    },
    ...buildNavOptions(menuOptions),
  ];

  const getPageTitle = () => {
    const currentPath = location.pathname;
    const currentNav = navOptions.find((opt) => currentPath.startsWith(opt.path));
    return currentNav?.label || 'SconApp';
  };

  const handleNavigate = (option: SidebarNavOption) => {
    if (option.children && option.children.length > 0) {
      setExpandedMenus(prev => ({
        ...prev,
        [option.path]: !prev[option.path],
      }));
      return;
    }
    if (option.page !== undefined) {
      navigate(option.path, { state: { page: option.page } });
    } else {
      navigate(option.path);
    }
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const isParentActive = (option: SidebarNavOption) => {
    if (location.pathname.startsWith(option.path)) return true;
    if (option.children) {
      return option.children.some(child => location.pathname.startsWith(child.path));
    }
    return false;
  };

  const touchStartX = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    // Swipe in the 30-80px band (not the very edge, to avoid iOS back gesture)
    // with a rightward travel > 50px opens sidebar on mobile
    if (isMobile && !sidebarOpen && touchStartX.current >= 30 && touchStartX.current <= 80 && dx > 50) {
      setSidebarOpen(true);
    }
  };

  return (
    <Box
      sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Sidebar — hidden entirely on mobile */}
      {!isMobile && (
        <Drawer
          variant={'persistent'}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          sx={{
            width: sidebarOpen ? 260 : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 260,
              boxSizing: 'border-box',
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              padding: '20px 12px',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, px: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
              }}
              onClick={() => handleNavigate({ icon: <HomeOutlined />, label: 'Início', path: '/home' })}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : 'transparent',
                }}
              >
                <img
                  src={SconIcon}
                  alt="SconApp"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
              SconApp
              </Typography>
            </Box>
            {!isMobile && (
              <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ color: 'text.secondary' }}>
                <ChevronLeft />
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, overflow: 'auto' }}>
            {navOptions.map((option) => {
              const isActive = !option.children && (
                location.pathname === option.path || location.pathname.startsWith(option.path + '/')
              );
              const parentActive = isParentActive(option);
              const isExpanded = expandedMenus[option.path] || false;
              const hasChildren = option.children && option.children.length > 0;

              return (
                <Box key={option.path + (option.page ?? '')}>
                  <Box
                    onClick={() => handleNavigate(option)}
                    data-testid={`sidebar-link-${option.label.toLowerCase().replace(/\s+/g, '-')}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      padding: '12px 14px',
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      backgroundColor: isActive
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(236, 64, 122, 0.15)'
                          : 'rgba(236, 64, 122, 0.1)'
                        : 'transparent',
                      color: isActive ? '#ec407a' : theme.palette.text.secondary,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.06)'
                          : 'rgba(0, 0, 0, 0.04)',
                        color: theme.palette.text.primary,
                      },
                    }}
                  >
                    <Box sx={{ fontSize: '1.25rem', display: 'flex' }}>{option.icon}</Box>
                    <Typography variant="body1" sx={{ fontWeight: isActive || parentActive ? 600 : 500, fontSize: '0.875rem', flex: 1 }}>
                      {option.label}
                    </Typography>
                    {hasChildren && (
                      isExpanded ? <ExpandLess sx={{ fontSize: '1rem' }} /> : <ExpandMore sx={{ fontSize: '1rem' }} />
                    )}
                  </Box>

                  {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, ml: 4, mt: 0.25, mb: 0.25 }}>
                        {option.children!.map((child) => {
                          const childActive = location.pathname === child.path
                          && location.state && (location.state as any).page === child.page;
                          return (
                            <Box
                              key={child.path + (child.page ?? '') + (child.label ?? '')}
                              onClick={() => handleNavigate(child)}
                              data-testid={`sidebar-link-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                padding: '8px 12px',
                                borderRadius: 2,
                                cursor: 'pointer',
                                backgroundColor: childActive
                                  ? theme.palette.mode === 'dark'
                                    ? 'rgba(236, 64, 122, 0.12)'
                                    : 'rgba(236, 64, 122, 0.08)'
                                  : 'transparent',
                                color: childActive ? '#ec407a' : theme.palette.text.secondary,
                                transition: 'all 0.15s ease',
                                fontSize: '0.8125rem',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.03)',
                                  color: theme.palette.text.primary,
                                },
                              }}
                            >
                              <Box sx={{ fontSize: '1.25rem', display: 'flex' }}>{child.icon}</Box>
                              <Typography variant="body2" sx={{ fontWeight: childActive ? 600 : 400 }}>
                                {child.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Box>

          <Divider sx={{ my: 2, opacity: 0.5 }} />

          <Box
            onClick={() => logout()}
            data-testid='btn-logout'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              padding: '12px 14px',
              borderRadius: 2.5,
              cursor: 'pointer',
              color: theme.palette.text.secondary,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.06)'
                  : 'rgba(0, 0, 0, 0.04)',
                color: theme.palette.text.primary,
              },
            }}
          >
            <Box sx={{ fontSize: '1.25rem', display: 'flex' }}>
              <LogoutOutlined />
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            Sair
            </Typography>
          </Box>
        </Drawer>
      )}

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflowY: 'auto',
          marginLeft: sidebarOpen && !isMobile ? 0 : 0,
          transition: 'margin-left 0.3s ease',
          pb: isMobile ? '72px' : 0,
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              {!isMobile && (!sidebarOpen) && (
                <IconButton onClick={() => setSidebarOpen(true)} sx={{ color: 'text.secondary' }} data-testid='btn-menu-hamburger'>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                {getPageTitle()}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={toggleTheme}
                sx={{
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.05)',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                {theme.palette.mode === 'dark' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                )}
              </IconButton>
              <UserMenu />
            </Box>
          </Toolbar>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 2, sm: 3 },
            animation: 'slideUp 0.3s ease',
          }}
        >
          <IosPwaBanner />
          {isAuthenticated && !emailConfirmed && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              Você ainda não confirmou seu e-mail. Isso é necessário para usar o SconApp por completo.
            </Alert>
          )}
          {isAuthenticated && !phoneConfirmed && whatsappSconappConfigured && (
            <Alert
              severity='warning'
              sx={{ mb: 2 }}
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={() => navigate('/profile')}
                >
                  Ir para o perfil
                </Button>
              }
            >
              Você ainda não confirmou seu telefone. Vá para o perfil confirmar.
            </Alert>
          )}
          {toolbar && (
            <Box sx={{ mb: 3 }}>
              {toolbar}
            </Box>
          )}
          {children}
        </Box>
      </Box>

      <AlertComponent />

      <UpgradeModal />

      {isDev && (
        <Chip
          label="DEV"
          size="small"
          sx={{
            position: 'fixed',
            bottom: 12,
            right: 12,
            zIndex: 9999,
            backgroundColor: '#e91e63',
            color: '#fff',
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 1,
            opacity: 0.85,
            pointerEvents: 'none',
          }}
        />
      )}

      <FeedbackButton />

      {isMobile && <BottomNav />}
    </Box>
  );
};